import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import auth from "../../fire";
import { signOut } from "firebase/auth";
import axiosClient from "../../axiosClient";
import { ClipLoader } from "react-spinners";
import SupersetLogoHeader from "../../components/SupersetLogoHeader/SupersetLogoHeader";
import MessageModal from "../../components/MessageModal/MessageModal";
import DeleteModal from "../../components/DeleteModal/DeleteModal";
import { useInstallPrompt } from "../../hooks/useInstallPrompt";
import { useUserRole } from "../../store/UserRoleContext";
import {
  ChevronRightIcon,
  DevicePhoneMobileIcon,
  ShieldCheckIcon,
  IdentificationIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  KeyIcon,
} from "@heroicons/react/24/outline";

interface ModalState {
  isOpen: boolean;
  type: string;
  title: string;
  message: string;
}

const Account: React.FC = () => {
  const navigate = useNavigate();
  const [user, loading, error] = useAuthState(auth);
  const { role } = useUserRole();

  const { environment, forceShowPrompt } = useInstallPrompt();

  const [isSignOutModalOpen, setIsSignOutModalOpen] = useState(false);

  const [verifyState, setVerifyState] = useState<
    "idle" | "sending" | "sent" | "error"
  >("idle");

  const [modalState, setModalState] = useState<ModalState>({
    isOpen: false,
    type: "",
    title: "",
    message: "",
  });

  const handleResendVerification = async () => {
    setVerifyState("sending");
    try {
      await axiosClient.post("/api/users/send-verification");
      setVerifyState("sent");
    } catch (e) {
      setVerifyState("error");
    }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login");
      return;
    }
  }, [user, loading, navigate]);

  const handleLogout = () => {
    signOut(auth)
      .then(() => {
        // Sign-out successful.
      })
      .catch((error: unknown) => {
        // An error happened.
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        console.error("Error signing out: " + errorMessage);
        setModalState({
          isOpen: true,
          type: "error",
          title: "Error Signing Out",
          message: "An error occured while signing out. Page will now refresh.",
        });
      });
  };

  return (
    <>
      <SupersetLogoHeader />

      <div className="flex flex-col w-full bg-gray-50/50 min-h-[calc(100vh-63px)]">
        <div className="w-full bg-white border-b border-gray-200">
          <ul className="divide-y divide-gray-200">
            <li>
              <div className="w-full flex items-center justify-between px-4 py-4">
                <div className="flex items-center min-w-0">
                  <IdentificationIcon className="mr-3 h-5 w-5 text-gray-500 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-700 truncate">
                      {user?.email}
                    </p>
                    {user &&
                      (user.emailVerified ? (
                        <span className="inline-flex items-center text-xs text-green-600">
                          <CheckCircleIcon className="mr-1 h-4 w-4" />
                          Verified
                        </span>
                      ) : (
                        <span className="inline-flex items-center text-xs text-amber-600">
                          <ExclamationCircleIcon className="mr-1 h-4 w-4" />
                          Not verified
                        </span>
                      ))}
                  </div>
                </div>
                {user && !user.emailVerified && (
                  <div className="flex-shrink-0 pl-3">
                    {verifyState === "sent" ? (
                      <span className="text-xs text-gray-500">Sent ✓</span>
                    ) : (
                      <button
                        type="button"
                        onClick={handleResendVerification}
                        disabled={verifyState === "sending"}
                        className="text-sm font-medium text-indigo-600 hover:text-indigo-700 disabled:opacity-50"
                      >
                        {verifyState === "sending" ? (
                          <ClipLoader
                            color="rgb(79 70 229)"
                            speedMultiplier={0.6}
                            size={"1rem"}
                          />
                        ) : verifyState === "error" ? (
                          "Retry"
                        ) : (
                          "Verify"
                        )}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </li>

            <li>
              <Link
                to="/account/change-email"
                className="w-full flex items-center justify-between px-4 py-4 text-sm font-medium text-left text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-colors"
              >
                <div className="flex items-center">
                  <EnvelopeIcon className="mr-3 h-5 w-5 text-gray-500" />
                  Change email
                </div>
                <ChevronRightIcon className="h-5 w-5 text-gray-400" />
              </Link>
            </li>

            <li>
              <Link
                to="/account/change-password"
                className="w-full flex items-center justify-between px-4 py-4 text-sm font-medium text-left text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-colors"
              >
                <div className="flex items-center">
                  <KeyIcon className="mr-3 h-5 w-5 text-gray-500" />
                  Change password
                </div>
                <ChevronRightIcon className="h-5 w-5 text-gray-400" />
              </Link>
            </li>

            <li>
              <button
                className={`w-full flex items-center justify-between px-4 py-4 text-sm font-medium text-left transition-colors ${
                  environment === "installed" || environment === "desktop"
                    ? "text-gray-400 cursor-not-allowed bg-gray-50"
                    : "text-gray-700 hover:bg-gray-50 active:bg-gray-100"
                }`}
                onClick={() => {
                  if (
                    environment !== "installed" &&
                    environment !== "desktop"
                  ) {
                    forceShowPrompt();
                  }
                }}
                disabled={
                  environment === "installed" || environment === "desktop"
                }
              >
                <div className="flex items-center">
                  <DevicePhoneMobileIcon
                    className={`mr-3 h-5 w-5 ${
                      environment === "installed" || environment === "desktop"
                        ? "text-gray-400"
                        : "text-gray-500"
                    }`}
                  />
                  Install App
                </div>
                <div className="flex items-center">
                  {(environment === "installed" ||
                    environment === "desktop") && (
                    <span className="text-gray-400 font-normal mr-2">
                      {environment === "installed"
                        ? "Installed"
                        : "Mobile Only"}
                    </span>
                  )}
                  <ChevronRightIcon
                    className={`h-5 w-5 ${
                      environment === "installed" || environment === "desktop"
                        ? "hidden"
                        : "text-gray-400"
                    }`}
                  />
                </div>
              </button>
            </li>

            {role === "Admin" && (
              <li>
                <Link
                  to="/admin-dashboard"
                  className="w-full flex items-center justify-between px-4 py-4 text-sm font-medium text-left text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center">
                    <ShieldCheckIcon className="mr-3 h-5 w-5 text-gray-500" />
                    Admin Dashboard
                  </div>
                  <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                </Link>
              </li>
            )}

            {/* Future account options can go here */}
          </ul>
        </div>

        <div className="p-4">
          <button
            type="button"
            className="inline-flex w-full justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            onClick={() => setIsSignOutModalOpen(true)}
          >
            Sign out
          </button>
        </div>
      </div>

      <DeleteModal
        isOpen={isSignOutModalOpen}
        onModalClose={() => setIsSignOutModalOpen(false)}
        onSubmit={handleLogout}
        deleteTitle="Confirm Sign Out"
        deleteMessage="Are you sure you want to sign out of your account?"
        buttonText="Sign Out"
      />

      <MessageModal
        isOpen={modalState.isOpen}
        onModalClose={() => {
          setModalState({ ...modalState, isOpen: false });
          window.location.reload();
        }}
        type={modalState.type}
        title={modalState.title}
        message={modalState.message}
      />
    </>
  );
};

export default Account;
